import {
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Tooltip,
  Typography,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import DownloadIcon from "@material-ui/icons/GetApp";
import AttachmentModel from "api/models/AttachmentModel";
import clsx from "clsx";
import { lang } from "i18nLang";
import { observer } from "mobx-react";
import { useRef } from "react";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "0.313rem 0.5rem",
    border: `1px solid ${theme.palette.gray[400]}`,
    borderRadius: 4,
    maxHeight: 42,
    "&:hover": {
      backgroundColor: theme.palette.gray[200],
      "& .show-on-hover": {
        display: "unset",
      },
    },
    "& .show-on-hover": {
      display: "none",
    },
  },
  title: {
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontWeight: 600,
    color: theme.palette.gray[600],

    "&.finished": {
      color: theme.palette.primary.main,
    },
  },
  caption: {
    color: theme.palette.gray[600],
    fontWeight: 300,
    fontSize: "0.625rem",
    position: "relative",
    top: -5,
  },
  uppercase: {
    textTransform: "uppercase",
  },
  captionError: {
    color: theme.palette.error.main,
  },
  link: {
    color: theme.palette.gray[600],
    cursor: "pointer",
  },
  buttonDownload: { padding: 4 },
  linkDownload: {
    display: "none",
  },
}));

export const BorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 10,
    borderRadius: 5,
    backgroundColor: theme.palette.gray[300],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: theme.palette.success.main,
  },
}))(LinearProgress);

interface IAttachmentCardProps {
  attachment: AttachmentModel;
  onDelete: (attachment: AttachmentModel) => void;
}

const AttachmentCard = observer(
  ({ attachment, onDelete }: IAttachmentCardProps) => {
    const classes = useStyles();
    const isUploading = attachment.isUploading();
    const isUploadFailed = attachment.isUploadFailed();
    const linkDownloadRef = useRef<HTMLAnchorElement>(null);
    const fileName = attachment.getOnlyName();

    const handleClickDelete = () => {
      onDelete(attachment);
    };

    const handleClickDownload = () => {
      linkDownloadRef.current?.click();
    };

    const renderFileName = (extraClass = "") => {
      return (
        <Tooltip title={fileName}>
          <Typography
            variant="body2"
            className={clsx(classes.title, extraClass)}
          >
            {fileName}
          </Typography>
        </Tooltip>
      );
    };

    return (
      <Grid container className={classes.container} direction="row">
        {isUploading && (
          <Grid item xs>
            {renderFileName()}
            <BorderLinearProgress
              variant="determinate"
              value={attachment.progress}
            />
          </Grid>
        )}
        {isUploadFailed && (
          <Grid item xs>
            {renderFileName("finished")}
            <Typography variant="caption" className={classes.caption}>
              <span className={classes.captionError}>
                {lang("attachments.uploadError")}
              </span>
              {` • `}
              <Link className={classes.link} onClick={handleClickDelete}>
                {lang("delete")}
              </Link>
            </Typography>
          </Grid>
        )}
        {!isUploading && !isUploadFailed && (
          <>
            <Grid item xs={9}>
              {renderFileName("finished")}
              <Typography variant="caption" className={classes.caption}>
                <span className={classes.uppercase}>
                  {`${attachment.getExtention()} • `}
                </span>
                <Link className={classes.link} onClick={handleClickDelete}>
                  {lang("delete")}
                </Link>
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <IconButton
                className={clsx(classes.buttonDownload, "show-on-hover")}
                onClick={handleClickDownload}
              >
                <DownloadIcon />
              </IconButton>
              <Link
                ref={linkDownloadRef}
                href={attachment.url}
                target="_blank"
                className={classes.linkDownload}
              >
                Download
              </Link>
            </Grid>
          </>
        )}
      </Grid>
    );
  }
);

export default AttachmentCard;
